import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { getFirestore, doc, runTransaction, collection, addDoc, getDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getSalesShippingSnapshot } from "./utils/shippingSnapshot.js";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCQ4_TkOu4i5H1j43E4dAG7Yv_6vgZ1P68",
  authDomain: "nexrise-internal.firebaseapp.com",
  projectId: "nexrise-internal",
  storageBucket: "nexrise-internal.firebasestorage.app",
  messagingSenderId: "493135056072",
  appId: "1:493135056072:web:cc5beedcf77c2999e069fd",
  measurementId: "G-8WN38C2XM3"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export { ref, uploadBytes, getDownloadURL };
export { GoogleAuthProvider, signInWithPopup, signOut };

const buildInterbranchRecipientContext = async (db, mocData) => {
  let sourceWarehouse = null;

  if (mocData?.sourceWarehouseID) {
    const sourceWarehouseSnap = await getDoc(doc(db, "warehouses", mocData.sourceWarehouseID));
    if (sourceWarehouseSnap.exists()) {
      sourceWarehouse = sourceWarehouseSnap.data();
    }
  }

  const recipientEmail =
    mocData?.shipToEmail ||
    mocData?.destinationContactEmail ||
    mocData?.shippingPersonEmail ||
    mocData?.billToEmail ||
    sourceWarehouse?.email ||
    "";

  return {
    sourceWarehouse,
    recipientEmail
  };
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const buildUniqueEmailList = (...emails) => {
  const seen = new Set();
  const uniqueEmails = [];

  emails.forEach((email) => {
    const normalized = normalizeEmail(email);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    uniqueEmails.push(String(email).trim());
  });

  return uniqueEmails;
};

const getFileExtensionFromUrl = (url = '') => {
  try {
    const decodedPath = decodeURIComponent(new URL(url).pathname || '');
    const match = decodedPath.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
  } catch (error) {
    const sanitized = String(url || '').split('?')[0];
    const match = sanitized.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : '';
  }
};

const buildAttachment = (url, baseName, preferredName = '') => {
  if (!url) return null;

  const preferredExtensionMatch = String(preferredName || '').match(/\.([a-z0-9]+)$/i);
  const extension = (preferredExtensionMatch?.[1] || getFileExtensionFromUrl(url) || 'pdf').toLowerCase();

  return {
    filename: `${baseName}.${extension}`,
    path: url
  };
};

/**
 * Automated Email Notification Helper
 * Sends an email using the Trigger Email extension by writing to the 'mail' collection.
 */
export const sendEmailNotification = async (db, type, mocData, orderData) => {
  try {
    const isInterbranchTransfer = mocData?.typeOfMOC === 'Interbranch Transfer';
    const salesShippingSnapshot = !isInterbranchTransfer ? getSalesShippingSnapshot(mocData, orderData) : null;
    const strictShippingEmail = !isInterbranchTransfer
      ? (
          mocData?.shippingSnapshot?.contactEmail ||
          mocData?.shippingPersonEmail ||
          mocData?.shipToEmail ||
          ''
        )
      : '';
    let customer = null;
    let recipientEmail = '';
    let recipientEmails = [];

    if (isInterbranchTransfer) {
      const interbranchContext = await buildInterbranchRecipientContext(db, mocData);
      recipientEmail = interbranchContext.recipientEmail;
      recipientEmails = recipientEmail ? [recipientEmail] : [];
    } else {
      const customerID = mocData?.customerID || orderData?.customerID;
      if (!customerID) {
        console.warn("Cannot send email: No customerID found.");
        return false;
      }

      // 1. Fetch Customer Details
      const custSnap = await getDoc(doc(db, "customers", customerID));
      if (!custSnap.exists()) {
        console.warn("Cannot send email: Customer document not found.");
        return false;
      }
      customer = custSnap.data();

      if (type === 'dispatched' || type === 'delivered') {
        recipientEmails = buildUniqueEmailList(
          strictShippingEmail || salesShippingSnapshot?.contactEmail,
          mocData?.billToEmail,
          orderData?.billingAddress?.email,
          customer.email,
          customer.contact_email
        );
        recipientEmail = recipientEmails[0] || '';
      } else {
        recipientEmail = salesShippingSnapshot?.contactEmail ||
                         mocData?.shipToEmail ||
                         orderData?.shippingAddress?.email ||
                         mocData?.billToEmail ||
                         orderData?.billingAddress?.email ||
                         customer.email ||
                         customer.contact_email ||
                         '';
        recipientEmails = recipientEmail ? [recipientEmail] : [];
      }
    }

    if (!recipientEmails.length) {
      console.warn(`Cannot send email: No recipient email address found for ${isInterbranchTransfer ? 'interbranch transfer' : 'customer dispatch'}.`);
      return false;
    }

    // 2. Determine Template
    let templateName = '';
    switch(type) {
      case 'scheduled': templateName = 'packing_scheduled'; break;
      case 'done': templateName = 'packing_done'; break;
      case 'dispatched': templateName = 'dispatch_template'; break;
      case 'delivered': templateName = 'delivered'; break;
      default: templateName = type;
    }
    
    // 3. Prepare Dynamic Data
    const trackingUrl = `${window.location.origin}/ops/delivered/${mocData.id || mocData.mocNo}`;
    
    // 4. Prepare Attachments for Dispatch Email
    const attachments = [];
    if (type === 'dispatched') {
      if (mocData.deliveryChallanUrl) {
        const deliveryChallanAttachment = buildAttachment(
          mocData.deliveryChallanUrl,
          `Delivery_Challan_${mocData.mocNo || 'MOC'}`,
          mocData.deliveryChallanName || ''
        );
        if (deliveryChallanAttachment) attachments.push(deliveryChallanAttachment);
      }
      if (mocData.vehicleProofUrl) {
        const vehicleProofAttachment = buildAttachment(
          mocData.vehicleProofUrl,
          `Vehicle_Proof_${mocData.mocNo || 'MOC'}`,
          mocData.vehicleProofName || ''
        );
        if (vehicleProofAttachment) attachments.push(vehicleProofAttachment);
      }
    }
    if (type === 'delivered' && mocData?.deliveryDetails?.podUrl) {
      const podAttachment = buildAttachment(
        mocData.deliveryDetails.podUrl,
        `POD_${mocData.mocNo || 'MOC'}`,
        mocData.deliveryDetails?.podName || ''
      );
      if (podAttachment) attachments.push(podAttachment);
    }
    if (type === 'delivered' && mocData?.taxInvoiceUrl) {
      const taxInvoiceAttachment = buildAttachment(
        mocData.taxInvoiceUrl,
        `Tax_Invoice_${mocData.mocNo || 'MOC'}`,
        mocData.taxInvoiceName || ''
      );
      if (taxInvoiceAttachment) attachments.push(taxInvoiceAttachment);
    }

    const emailData = {
      to: recipientEmails.length === 1 ? recipientEmails[0] : recipientEmails.join(', '),
      message: {
        attachments: attachments
      },
      template: {
        name: templateName,
        data: {
          customerName: isInterbranchTransfer
            ? (mocData.destinationWarehouseName || mocData.customerName || mocData.shippingPersonName || "Warehouse")
            : (salesShippingSnapshot?.customerName || customer?.display_name || customer?.company_name || customer?.name || "Customer"),
          orderNumber: orderData?.orderNumber || mocData?.orderNumber || mocData?.poNumber || "---",
          mocNo: mocData?.mocNo || "---",
          trackingUrl: trackingUrl,
          // Dispatch Specific Data
          quantityVerifiedByDispatch: mocData.quantityVerifiedByDispatch || "Yes",
          dispatchedThrough: mocData.dispatchedThrough || "---",
          driverName: mocData.driverName || "---",
          driverContact: mocData.driverContact || "---",
          vehicleNumber: mocData.vehicleNumber || "---",
          vehicleProofUrl: mocData.vehicleProofUrl || "",
          deliveryChallanUrl: mocData.deliveryChallanUrl || "",
          deliveryDate: mocData.deliveryDetails?.deliveryDate || "---",
          receiverName: mocData.deliveryDetails?.receiverName || "---",
          receiverContact: mocData.deliveryDetails?.receiverContact || "---",
          deliveryConfirmedBy: mocData.deliveryDetails?.deliveryConfirmedBy || "---",
          podUrl: mocData.deliveryDetails?.podUrl || ""
        }
      }
    };

    // 5. Drop the 'letter' into the 'mail' collection
    await addDoc(collection(db, "mail"), emailData);
    console.log(`Email queued successfully: ${templateName} to ${recipientEmails.join(', ')}`);
    
    return true;
  } catch (error) {
    console.error("Error sending email notification:", error);
    return false;
  }
};

const getFirmPrefix = (firmId) => {
  if (firmId.startsWith('nexrise')) return 'NIIPL';
  if (firmId.startsWith('jagdish')) return 'JE';
  if (firmId.startsWith('cloudnine')) return 'C9';
  return 'GEN';
};

export const getNextVoucherNumber = async (db, firmId) => {
  const firmPrefix = getFirmPrefix(firmId);
  const counterKey = `invoice_${firmPrefix.toLowerCase()}_count`;
  const sequencesRef = doc(db, "counters", "sequences");
  const yearCode = "25-26";

  try {
    const newCounter = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      if (!sequenceDoc.exists()) {
        throw new Error("Counter document does not exist!");
      }
      const currentCount = sequenceDoc.data()[counterKey] || 1000;
      const nextCount = currentCount + 1;
      transaction.update(sequencesRef, { [counterKey]: nextCount });
      return currentCount;
    });

    return `${firmPrefix}/${yearCode}/IN${newCounter}`;
  } catch (error) {
    console.error("Error getting next voucher number: ", error);
    return null;
  }
};

export const getNextMocNumber = async (db) => {
  const sequencesRef = doc(db, "counters", "moc_sequence");

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      
      let currentCount = 4900;
      if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
        currentCount = sequenceDoc.data().count;
      }
      
      const nextCount = currentCount + 1;
      transaction.set(sequencesRef, { count: nextCount }, { merge: true });
      return currentCount;
    });

    return result.toString();
  } catch (error) {
    console.error("Error getting next MOC number: ", error);
    return "";
  }
};

export const getNextSequenceNumber = async (db, firmId, typePrefix, counterCollection = "sequences") => {
  const firmPrefix = getFirmPrefix(firmId);
  const counterKey = `${typePrefix.toLowerCase()}_${firmPrefix.toLowerCase()}_count`;
  const sequencesRef = doc(db, "counters", counterCollection);
  const yearCode = new Date().getFullYear().toString().slice(-2); // Current year in YY format

  try {
    const newCounter = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      
      let currentCount = 1000; // Default starting count
      if (sequenceDoc.exists()) {
        currentCount = sequenceDoc.data()[counterKey] || 1000;
      } else {
        // If the main sequences document doesn't exist, create it with initial counts
        transaction.set(sequencesRef, {
          [counterKey]: currentCount
        }, { merge: true }); // Use merge: true to avoid overwriting other counters if doc exists
        return currentCount;
      }
      
      const nextCount = currentCount + 1;
      transaction.update(sequencesRef, { [counterKey]: nextCount });
      return currentCount;
    });

    return `${firmPrefix}/${yearCode}/${typePrefix.toUpperCase()}${newCounter}`;
  } catch (error) {
    console.error(`Error getting next ${typePrefix} number: `, error);
    return null;
  }
};
